import { useDebouncedValue } from '@/shared/utils';
import {
  Combobox,
  Field,
  Spinner,
} from '@noorden-insurtech/alba-design-system';
import { useMemo, useState } from 'react';
import { usePixParticipants } from '../../hooks';

type PixBank = { ispb: string; bankName: string };

interface PixBankComboboxProps {
  value?: PixBank;
  onChange: (bank: PixBank | undefined) => void;
  error?: string;
  disabled?: boolean;
}

/**
 * Banco do segurado para o PIX Automático: busca na lista de participantes do
 * PIX (Finance.API) por nome ou ISPB. O valor é o ISPB, que é o que o mandato
 * exige — o nome vai junto só para exibir.
 */
export function PixBankCombobox({
  value,
  onChange,
  error,
  disabled,
}: PixBankComboboxProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data: participants = [], isLoading } = usePixParticipants(
    debouncedSearch,
    !disabled
  );

  const items = useMemo(() => {
    const options = participants.map(item => ({
      value: item.ispb,
      label: item.name,
    }));
    // O selecionado precisa estar na coleção mesmo fora da busca atual, senão o
    // Combobox perde o rótulo ao fechar.
    if (value?.ispb && !options.some(item => item.value === value.ispb)) {
      options.unshift({ value: value.ispb, label: value.bankName });
    }
    return options;
  }, [participants, value]);

  return (
    <Field.Root invalid={!!error} required disabled={disabled}>
      <Field.Label className='text-alba-black-500'>Banco</Field.Label>
      <Combobox.Root
        items={items}
        value={value?.ispb ? [value.ispb] : []}
        onValueChange={details => {
          const ispb = details.value[0];
          const item = items.find(option => option.value === ispb);
          onChange(
            item ? { ispb: item.value, bankName: item.label } : undefined
          );
        }}
        onInputValueChange={details => setSearch(details.inputValue)}
        selectionBehavior='replace'
        disabled={disabled}
      >
        <Combobox.Control>
          <Combobox.Trigger>
            {value?.bankName ?? 'Selecione o banco do segurado'}
          </Combobox.Trigger>
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content>
            <Combobox.Input
              placeholder='Buscar banco por nome ou ISPB...'
              autoFocus
            />
            {isLoading ? (
              <div className='flex items-center justify-center py-8'>
                <Spinner size='sm' colorScheme='blue' />
              </div>
            ) : (
              <Combobox.List>
                {items.map(item => (
                  <Combobox.Item key={item.value} item={item}>
                    <Combobox.ItemText>{item.label}</Combobox.ItemText>
                    <Combobox.ItemIndicator />
                  </Combobox.Item>
                ))}
                {search.trim().length > 0 && <Combobox.Empty />}
              </Combobox.List>
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>
      {error && <Field.ErrorText>{error}</Field.ErrorText>}
    </Field.Root>
  );
}
